<template>
  <div class="app-shell">
    <div v-if="menuOpen" class="app-menu-backdrop" @click="menuOpen = false"></div>

    <aside class="app-menu" :class="{ open: menuOpen }" aria-label="User menu" :aria-hidden="!menuOpen">
      <div class="app-menu-head">
        <div class="app-menu-user">
          <div class="user-name-line"><strong>{{ userName }}</strong><span v-if="userPremium" class="premium-crown" title="Premium" aria-label="Premium">♛</span></div>
          <span>{{ shortKey(user?.public_key) }}</span>
        </div>
        <button class="app-menu-close" type="button" aria-label="Close menu" @click="menuOpen = false">x</button>
      </div>

      <nav class="app-menu-nav" aria-label="Workspaces">
        <button
          type="button"
          class="app-menu-link"
          :class="{ active: walletsOpen }"
          @click="openWallets"
        >
          <span>My wallets</span>
          <small>Wallets & positions</small>
        </button>
      </nav>

      <div class="app-menu-footer">
        <button class="app-menu-logout" type="button" @click="logout">
          <span class="logout-icon" aria-hidden="true"></span>
          <span>Logout</span>
        </button>
      </div>
    </aside>

    <header>
      <div class="brand">
        <button class="user-trigger" type="button" aria-label="Open user menu" @click="menuOpen = true">
          <div class="user-name-line"><strong>{{ userName }}</strong><span v-if="userPremium" class="premium-crown" title="Premium" aria-label="Premium">♛</span></div>
          <span>{{ shortKey(user?.public_key) }}</span>
        </button>
      </div>

      <nav class="workspace-switcher" aria-label="Workspaces">
        <button
          v-for="workspace in headerWorkspaces"
          :key="workspace.id"
          type="button"
          :class="{ active: workspace.id === activeWorkspace.id }"
          @click="selectWorkspace(workspace.id)"
        >
          {{ workspace.label }}
        </button>
      </nav>

      <div class="actions">
        <div class="account-summary" :title="`Today · win rate ${wrValue}`">
          <button class="balance-trigger" type="button" title="Transfer from main wallet" @click="mainTransferOpen = true"><small>Balance</small><strong>{{ balanceValue }}</strong></button>
          <span><small>PnL · today</small><strong :class="pnlClass">{{ pnlValue }}</strong></span>
          <i class="wr-track" aria-hidden="true"><i :style="{ width: `${wrPercent}%` }"></i></i>
        </div>
      </div>
    </header>

    <div class="app-page">
      <KeepAlive :max="6">
        <component :is="renderWorkspace.component" v-bind="renderWorkspace.props || {}" />
      </KeepAlive>
    </div>

    <nav class="bottom-nav" aria-label="Primary navigation">
      <button type="button" :class="{ on: bottomSection === 'home' }" @click="openHome">
        <span class="nav-icon nav-home" aria-hidden="true"></span>
        Play
      </button>
      <button type="button" :class="{ on: bottomSection === 'nft' }" @click="openNft">
        <span class="nav-icon nav-nft" aria-hidden="true"></span>
        NFT
      </button>
      <button type="button" :class="{ on: bottomSection === 'plugins' }" @click="openPlugins">
        <span class="nav-icon nav-plugins" aria-hidden="true"></span>
        Plugins
      </button>
    </nav>
    <TransferModal :visible="mainTransferOpen" source-kind="main" :user="user" :wallets="walletList" @close="mainTransferOpen = false" />
    <div v-if="walletsOpen" class="wallets-overlay" @click.self="walletsOpen = false">
      <section class="wallets-modal" aria-label="My wallets">
        <IconAction class="wallets-close" name="close" label="Close wallets" :size="30" @click="walletsOpen = false" />
        <WalletList />
      </section>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useStorage } from '@/stores'
import { allWorkspaces, fallbackWorkspace } from '@/shell/workspaces'
import { sumWalletPnl, walletRows, winrateText } from '@/shell/pnl'
import { shortKey, formatPnlWithPct, formatSol } from '@/components/_shared/format'
import ConsoleWorkspace from '@/workspaces/console/ConsoleWorkspace.vue'
import TransferModal from '@/components/wallet/transfer.vue'
import IconAction from '@/components/_shared/IconAction.vue'
import WalletList from '@/components/wallet/list.vue'
import { makePluginsApi } from '@/stores/api/plugins'
import { reportClientError } from '@/stores/api/clientErrors'

const ACTIVE_WORKSPACE_KEY = 'aime.workspace.active'
const ACTIVE_OWNER_DRAFT_KEY = 'aime.workspace.ownerDraft'

const store = useStorage()
const pluginsApi = makePluginsApi()
const installationsResolveStartedAt = performance.now()
if (typeof performance.mark === 'function') performance.mark('aime:installations-resolve-start')
const menuOpen = ref(false)
const mainTransferOpen = ref(false)
const walletsOpen = ref(false)
const activeWorkspaceId = ref(readActiveWorkspaceId())
const nftFilterPluginId = ref(String(new URLSearchParams(window.location.search).get('plugin_id') || '').trim())
const nftCharacterLink = ref(readNftCharacterLink())
const bottomSection = ref(activeWorkspaceId.value === 'nft' ? 'nft' : activeWorkspaceId.value === 'plugins' ? 'plugins' : 'home')
const workspaceSelectionTouched = ref(false)
const ownerPlugins = ref([])
const ownerPluginsReady = ref(false)
const selectedOwnerDraft = ref(readSelectedOwnerDraft())

const user = computed(() => store.user)
const userName = computed(() => {
  const u = user.value || {}
  return String(u.name || u.username || u.display_name || u.displayName || 'AiMe').trim() || 'AiMe'
})

const workspaces = computed(() => allWorkspaces(store.uiPluginInstallations || []))
const headerWorkspaces = computed(() => workspaces.value)
const activeExtensionWorkspace = computed(() => workspaces.value.find(item => item.kind === 'extension' && item.installation?.active === true) || null)
const activeWorkspace = computed(() => {
  if (selectedOwnerDraft.value && bottomSection.value === 'home') return workspaces.value.find(item => item.id === 'console' && item.component) || fallbackWorkspace()
  return workspaces.value.find(item => item.id === activeWorkspaceId.value && item.component) || activeExtensionWorkspace.value || fallbackWorkspace()
})
const activeOwnerDraft = computed(() => {
  const selected = selectedOwnerDraft.value
  if (!selected?.pluginId || !selected?.versionId) return null
  const plugin = ownerPlugins.value.find(item => String(item?.id || '') === selected.pluginId)
  const draft = plugin?.draft
  if (draft?.status !== 'draft' || String(draft.id || '') !== selected.versionId) return null
  if (Number(draft.version || 0) <= Number(plugin.published_version || 0)) return null
  return ownerDraftDescriptor(plugin, draft)
})
const renderWorkspace = computed(() => {
  const workspace = activeWorkspace.value
  if (workspace.id === 'nft') {
    return {
      ...workspace,
      props: {
        pluginId: nftFilterPluginId.value,
        characterId: nftCharacterLink.value?.characterId || '',
        wallet: nftCharacterLink.value?.wallet || '',
      },
    }
  }
  if (workspace.id === 'console') {
    return {
      ...workspace,
      props: { presentationReady: store.installationsReady && ownerPluginsReady.value, ownerDraft: activeOwnerDraft.value },
    }
  }
  if (workspace.role !== 'presentation') return workspace
  return {
    ...workspace,
    component: ConsoleWorkspace,
    props: {
      presentationComponent: workspace.component,
      presentationProps: { installation: workspace.installation },
      presentationReady: ownerPluginsReady.value,
    },
  }
})

watch(activeExtensionWorkspace, workspace => {
  if (!workspace || workspaceSelectionTouched.value) return
  const current = workspaces.value.find(item => item.id === activeWorkspaceId.value && item.component) || null
  if (current?.kind === 'extension' && current.installation?.active !== true) {
    setWorkspace(workspace, 'home')
    return
  }
  if (activeWorkspaceId.value !== 'console' && current) return
  setWorkspace(workspace, 'home')
}, { immediate: true })

watch(activeWorkspace, workspace => {
  if (!workspace) return
  if (workspace.id !== activeWorkspaceId.value) activeWorkspaceId.value = workspace.id
  bottomSection.value = workspace.id === 'nft' ? 'nft' : workspace.id === 'plugins' ? 'plugins' : 'home'
})

onMounted(async () => {
  await Promise.all([hydrateInstallations(), hydrateOwnerPlugins()])
})

window.addEventListener('aime-shell-workspace', onShellWorkspace)
window.addEventListener('aime-shell-section', onShellSection)
window.addEventListener('aime-shell-wallet-trades', onShellWalletTrades)
window.addEventListener('aime-shell-character', onShellCharacter)
window.addEventListener('aime-shell-back', onShellBack)
window.addEventListener('aime-draft-preview', onOwnerDraftPreview)
window.addEventListener('aime-draft-preview-clear', clearOwnerDraftPreview)

onBeforeUnmount(() => {
  window.removeEventListener('aime-shell-workspace', onShellWorkspace)
  window.removeEventListener('aime-shell-section', onShellSection)
  window.removeEventListener('aime-shell-wallet-trades', onShellWalletTrades)
  window.removeEventListener('aime-shell-character', onShellCharacter)
  window.removeEventListener('aime-shell-back', onShellBack)
  window.removeEventListener('aime-draft-preview', onOwnerDraftPreview)
  window.removeEventListener('aime-draft-preview-clear', clearOwnerDraftPreview)
})

const walletList = computed(() => walletRows(store.wallets || {}))
const userPremium = computed(() => user.value?.premium === true || walletList.value.some(wallet => wallet?.premium === true))
const balanceValue = computed(() => { const value = user.value?.lamports ?? user.value?.Lamports; return value == null ? '— SOL' : `${formatSol(Number(value))} SOL` })
const userPnl = computed(() => sumWalletPnl(walletList.value, 'day'))
const pnlLamports = computed(() => userPnl.value.lamports)
const pnlCostLamports = computed(() => userPnl.value.costLamports)
const pnlValue = computed(() => formatPnlWithPct(pnlLamports.value, 0))
const wrValue = computed(() => winrateText(store.pnl || {}, Object.values(store.coins || {})))
const wrPercent = computed(() => {
  const value = Number.parseFloat(String(wrValue.value).replace('%', ''))
  return Number.isFinite(value) ? Math.max(0, Math.min(100, value)) : 0
})
const pnlClass = computed(() => {
  if (pnlLamports.value > 0) return 'positive'
  if (pnlLamports.value < 0) return 'negative'
  return 'neutral'
})
async function hydrateOwnerPlugins() {
  try {
    const result = await pluginsApi.listPlugins()
    ownerPlugins.value = Array.isArray(result?.items) ? result.items : []
    if (selectedOwnerDraft.value && !activeOwnerDraft.value) clearOwnerDraftPreview()
  } catch (e) {
    reportClientError('warn', 'owner_plugins_resolve_failed', { reason: e?.message || String(e) })
  } finally {
    ownerPluginsReady.value = true
  }
}

async function hydrateInstallations() {
  if (store.installationsReady) {
    reportClientError('info', 'installations_resolve', {
      source: 'snapshot',
      totalMs: Math.round((performance.now() - installationsResolveStartedAt) * 10) / 10,
    })
    return
  }

  const requestStartedAt = performance.now()
  try {
    const result = await pluginsApi.listInstallations()
    if (!store.installationsReady) store.setUIPluginInstallations(result?.items)
    reportClientError('info', 'installations_resolve', {
      source: store.installationsReady ? 'api_or_snapshot' : 'api',
      requestMs: Math.round((performance.now() - requestStartedAt) * 10) / 10,
      totalMs: Math.round((performance.now() - installationsResolveStartedAt) * 10) / 10,
      count: Array.isArray(result?.items) ? result.items.length : 0,
    })
  } catch (e) {
    // The first websocket snapshot remains the authoritative fallback.
    reportClientError('warn', 'installations_resolve_failed', {
      requestMs: Math.round((performance.now() - requestStartedAt) * 10) / 10,
      totalMs: Math.round((performance.now() - installationsResolveStartedAt) * 10) / 10,
      reason: e?.message || String(e),
    })
  }
}

function ownerDraftDescriptor(plugin, draft) {
  return {
    pluginId: String(plugin?.id || ''),
    versionId: String(draft?.id || ''),
    version: Number(draft?.version || 0),
    name: String(plugin?.name || plugin?.slug || 'Extension').trim() || 'Extension',
    semanticVersion: String(draft?.semantic_version || '0.1.0-dev').trim() || '0.1.0-dev',
    sdkVersion: String(draft?.sdk_version || plugin?.sdk_version || 'aime.ui-plugin.sdk.v1').trim() || 'aime.ui-plugin.sdk.v1',
  }
}

function readSelectedOwnerDraft() {
  try {
    const parsed = JSON.parse(window.localStorage?.getItem(ACTIVE_OWNER_DRAFT_KEY) || 'null')
    const pluginId = String(parsed?.pluginId || '').trim()
    const versionId = String(parsed?.versionId || '').trim()
    return pluginId && versionId ? { pluginId, versionId } : null
  } catch {
    return null
  }
}

function onOwnerDraftPreview(event) {
  const detail = event?.detail && typeof event.detail === 'object' ? event.detail : {}
  const pluginId = String(detail.pluginId || '').trim()
  const versionId = String(detail.versionId || '').trim()
  if (!pluginId || !versionId) return
  selectedOwnerDraft.value = { pluginId, versionId }
  window.localStorage?.setItem(ACTIVE_OWNER_DRAFT_KEY, JSON.stringify(selectedOwnerDraft.value))
}

function clearOwnerDraftPreview() {
  selectedOwnerDraft.value = null
  window.localStorage?.removeItem(ACTIVE_OWNER_DRAFT_KEY)
}

function readNftCharacterLink() {
  const query = new URLSearchParams(window.location.search)
  const pluginId = String(query.get('plugin_id') || '').trim()
  const characterId = String(query.get('character_id') || '').trim()
  if (!pluginId || !characterId) return null
  return { pluginId, characterId, wallet: String(query.get('wallet') || '').trim() }
}

function readActiveWorkspaceId() {
  const requested = String(new URLSearchParams(window.location.search).get('workspace') || '').trim()
  if (requested) return requested
  return String(window.localStorage?.getItem(ACTIVE_WORKSPACE_KEY) || 'console').trim() || 'console'
}

function setWorkspace(workspace, section = null) {
  if (!workspace || !workspace.component) return
  activeWorkspaceId.value = workspace.id
  bottomSection.value = section || (workspace.id === 'nft' ? 'nft' : workspace.id === 'plugins' ? 'plugins' : 'home')
  window.localStorage?.setItem(ACTIVE_WORKSPACE_KEY, workspace.id)
  menuOpen.value = false
}

function selectWorkspace(id) {
  const next = String(id || '').trim()
  const workspace = workspaces.value.find(item => item.id === next)
  if (!workspace || !workspace.component) return
  workspaceSelectionTouched.value = true
  setWorkspace(workspace)
}

function onShellWorkspace(event) {
  const detail = event?.detail && typeof event.detail === 'object' ? event.detail : {}
  const pluginId = String(detail?.filter?.pluginId || '').trim()
  if (detail.workspace === 'nft' && pluginId) {
    nftFilterPluginId.value = pluginId
    nftCharacterLink.value = null
  }
  selectWorkspace(detail.workspace)
  if (detail.workspace !== 'nft' || !pluginId) return
  const url = new URL(window.location.href)
  url.searchParams.set('workspace', 'nft')
  url.searchParams.set('plugin_id', pluginId)
  url.searchParams.delete('character_id')
  url.searchParams.delete('wallet')
  window.history.replaceState({}, '', url)
  nextTick(() => {
    window.dispatchEvent(new CustomEvent('aime-nft-filter', { detail: { pluginId } }))
  })
}

function onShellCharacter(event) {
  const d = event?.detail && typeof event.detail === 'object' ? event.detail : {}
  const pluginId = String(d.pluginId || '').trim()
  const characterId = String(d.characterId || '').trim()
  if (!pluginId || !characterId) return
  const wallet = String(d.wallet || '').trim()
  nftFilterPluginId.value = pluginId
  nftCharacterLink.value = { pluginId, characterId, wallet }
  const url = new URL(window.location.href)
  url.searchParams.set('workspace', 'nft')
  url.searchParams.set('plugin_id', pluginId)
  url.searchParams.set('character_id', characterId)
  if (wallet) url.searchParams.set('wallet', wallet)
  else url.searchParams.delete('wallet')
  window.history.replaceState({}, '', url)
  selectWorkspace('nft')
  nextTick(() => {
    window.dispatchEvent(new CustomEvent('aime-nft-filter', { detail: { pluginId } }))
    window.dispatchEvent(new CustomEvent('aime-nft-character', { detail: { pluginId, characterId, wallet: String(d.wallet || '').trim() } }))
  })
}

function onShellBack() {
  const workspace = activeExtensionWorkspace.value || workspaces.value.find(item => item.id === 'console') || fallbackWorkspace()
  workspaceSelectionTouched.value = true
  setWorkspace(workspace, 'home')
  const url = new URL(window.location.href)
  url.searchParams.delete('workspace')
  window.history.replaceState({}, '', url)
}

function onShellSection(event) {
  const section = String(event?.detail?.section || '').trim()
  if (section === 'home' || section === 'plugins') bottomSection.value = section
}

function onShellWalletTrades(event) {
  const wallet = String(event?.detail?.wallet || '').trim()
  if (!wallet) return
  walletsOpen.value = false
  const current = activeWorkspace.value
  if (current?.role !== 'presentation' || current.id === 'console') {
    const workspace = activeExtensionWorkspace.value || workspaces.value.find(item => item.id === 'console') || fallbackWorkspace()
    workspaceSelectionTouched.value = true
    setWorkspace(workspace, 'home')
  }
  bottomSection.value = 'home'
  nextTick(() => {
    window.dispatchEvent(new CustomEvent('aime-console-section', { detail: { section: 'home' } }))
    window.dispatchEvent(new CustomEvent('aime-wallet-trades', { detail: { wallet } }))
  })
}

function openHome() {
  const workspace = activeOwnerDraft.value
    ? workspaces.value.find(item => item.id === 'console') || fallbackWorkspace()
    : activeExtensionWorkspace.value || workspaces.value.find(item => item.id === 'console') || fallbackWorkspace()
  workspaceSelectionTouched.value = true
  setWorkspace(workspace, 'home')
  nextTick(() => window.dispatchEvent(new CustomEvent('aime-console-section', { detail: { section: 'home' } })))
}

function openNft() {
  selectWorkspace('nft')
}

function openPlugins() {
  selectWorkspace('plugins')
}

function openWallets() {
  walletsOpen.value = true
  menuOpen.value = false
}

function logout() {
  menuOpen.value = false
  store.logout()
}
</script>

<style scoped>
.app-shell {
  --app-bottom-nav-space: 58px;
  height: 100vh;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: #0a0a0a;
  overflow: hidden;
}

.app-shell header {
  flex: 0 0 auto;
  position: relative;
  display: grid;
  grid-template-columns: minmax(140px, 240px) minmax(0, 1fr) auto;
  align-items: center;
  gap: 16px;
  padding: 12px 24px;
  background: #111;
  border-bottom: 1px solid #222;
  height: 60px;
}

.app-page {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
}

.brand {
  min-width: 0;
  display: flex;
  align-items: center;
}

.user-trigger {
  min-width: 0;
  display: grid;
  gap: 2px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #fff;
  text-align: left;
  cursor: pointer;
  font-family: inherit;
}

.user-name-line{min-width:0;display:flex;align-items:center;gap:5px}.premium-crown{flex:0 0 auto;color:#facc15!important;font-size:13px!important;line-height:1;text-shadow:0 0 8px #f59e0b66}.app-menu-user .user-name-line strong{font-size:18px}
.user-trigger strong {
  font-size: 14px;
  font-weight: 800;
  line-height: 1.1;
}

.user-trigger span {
  color: #97a6bf;
  font-size: 13px;
  line-height: 1.15;
}

.workspace-switcher {
  min-width: 0;
  display: flex;
  justify-content: center;
  gap: 6px;
  overflow-x: auto;
  scrollbar-width: none;
}

.workspace-switcher::-webkit-scrollbar { display: none; }

.workspace-switcher button {
  flex: 0 0 auto;
  height: 34px;
  padding: 0 12px;
  border: 1px solid #27333a;
  border-radius: 8px;
  background: #0d1416;
  color: #9fb0c7;
  font: inherit;
  font-size: 13px;
  font-weight: 800;
  cursor: pointer;
}

.workspace-switcher button:hover,
.workspace-switcher button.active {
  border-color: #35c6a4;
  color: #eafff8;
  background: #10201d;
}

.actions {
  width: 100%;
  min-width: 0;
  display: grid;
  grid-template-columns: minmax(116px, max-content);
  align-items: center;
  justify-content: end;
  gap: 8px;
}

.account-summary{min-width:116px;display:grid;gap:2px;font-variant-numeric:tabular-nums}.account-summary>span,.balance-trigger{display:flex;align-items:baseline;justify-content:space-between;gap:9px;line-height:1}.balance-trigger{padding:0;border:0;background:transparent;color:inherit;cursor:pointer}.balance-trigger:hover strong{color:#60a5fa}.account-summary small{color:#6f8197;font-size:8px;font-weight:800;text-transform:uppercase;letter-spacing:.04em}.account-summary strong{color:#e5e7eb;font-size:10px;font-weight:850;white-space:nowrap}.account-summary strong.positive{color:#22c55e}.account-summary strong.negative{color:#ef4444}.account-summary strong.neutral{color:#dbeafe}.wr-track{position:relative;height:2px;margin-top:2px;overflow:hidden;border-radius:999px;background:#26313b}.wr-track>i{position:absolute;inset:0 auto 0 0;min-width:1px;border-radius:inherit;background:linear-gradient(90deg,#ef4444,#f59e0b 50%,#22c55e)}

.app-menu-backdrop { position: fixed; inset: 0; z-index: 119; background: rgb(0 0 0 / 55%); }
.app-menu { position: fixed; inset: 0 auto 0 0; z-index: 120; width: min(320px, 88vw); max-height: 100dvh; padding: 14px; padding-bottom: max(14px, env(safe-area-inset-bottom)); display: flex; flex-direction: column; background: #0d1416; border-right: 1px solid #233035; transform: translateX(-100%); transition: transform .18s ease; }
.app-menu.open { transform: translateX(0); }
.app-menu-head { flex: 0 0 auto; min-height: 38px; display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 12px; }
.app-menu-user { min-width: 0; display: grid; gap: 3px; }
.app-menu-user strong { color: #eef6f4; font-size: 18px; font-weight: 800; }
.app-menu-user span { color: #91a2a0; font-size: 12px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.app-menu-close { width: 34px; height: 34px; border: 1px solid #2b3a40; border-radius: 8px; background: #101719; color: #eef6f4; cursor: pointer; }
.app-menu-nav { display: grid; grid-auto-rows:max-content; align-content:start; gap: 6px; overflow-y: auto; min-height: 0; flex: 1 1 auto; padding-right: 2px; }
.wallets-overlay{position:fixed;inset:0;z-index:9000;display:flex;align-items:center;justify-content:center;padding:14px;background:rgba(0,0,0,.72)}.wallets-modal{position:relative;width:min(980px,100%);height:min(720px,calc(100vh - 28px));min-height:0}.wallets-close{position:absolute!important;z-index:22;top:9px;right:9px}.wallets-modal :deep(.panel){height:100%}
.app-menu-link { width: 100%; min-width: 0; min-height: 44px; padding: 8px 10px; border: 1px solid #233035; border-radius: 8px; background:#11191b; color:#eef6f4; text-align:left; font:inherit; cursor:pointer; }
.app-menu-link span,
.app-menu-link small { display:block; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.app-menu-link span { font-size:14px; font-weight:800; }
.app-menu-link small { margin-top:3px; color:#8fa1b5; font-size:11px; }
.app-menu-link.active,
.app-menu-link:hover { border-color:#35c6a4; background:#10201d; }
.app-menu-footer { flex:0 0 auto; padding-top:10px; }
.app-menu-logout { width:100%; height:40px; display:inline-flex; align-items:center; justify-content:center; gap:10px; border:1px solid #382327; border-radius:8px; background:#180f11; color:#fecaca; font:inherit; font-weight:800; cursor:pointer; }

.bottom-nav { display:none; }
.nav-icon { position:relative; width:13px; height:13px; flex:0 0 13px; opacity:.85; }
.nav-home::before { content:""; position:absolute; left:1px; right:1px; bottom:1px; height:7px; border:2px solid currentColor; border-top:0; border-radius:1px; }
.nav-home::after { content:""; position:absolute; left:2px; top:1px; width:9px; height:9px; border-left:2px solid currentColor; border-top:2px solid currentColor; transform:rotate(45deg); border-radius:1px; }
.nav-nft::before { content:""; position:absolute; left:1px; right:1px; top:3px; height:8px; border:2px solid currentColor; border-radius:2px; }
.nav-nft::after { content:""; position:absolute; right:1px; top:5px; width:4px; height:4px; border:1px solid currentColor; border-radius:1px; }
.nav-plugins::before { content:""; position:absolute; border:2px solid currentColor; border-radius:3px; left:1px; top:1px; width:5px; height:5px; box-shadow:0 7px 0 -2px currentColor,7px 0 0 -2px currentColor,7px 7px 0 -2px currentColor; }
.logout-icon { position:relative; width:14px; height:18px; display:inline-block; border:2px solid currentColor; border-left-width:3px; border-radius:2px; }
.logout-icon::before { content:""; position:absolute; right:-6px; top:7px; width:8px; height:2px; background:currentColor; border-radius:999px; }
.logout-icon::after { content:""; position:absolute; right:-7px; top:5px; width:6px; height:6px; border-top:2px solid currentColor; border-right:2px solid currentColor; transform:rotate(45deg); }

@media (max-width:760px) {
  .app-shell header { grid-template-columns:minmax(0,1fr) minmax(116px,auto); gap:8px; padding:8px 12px; }
  .workspace-switcher { display:none; }
  .actions { justify-self:end; }
  .account-summary{min-width:116px}

  .bottom-nav {
    position:fixed;
    left:8px;
    right:8px;
    bottom:max(6px,env(safe-area-inset-bottom));
    z-index:80;
    height:46px;
    display:grid;
    grid-template-columns:repeat(3,minmax(0,1fr));
    gap:6px;
    padding:6px;
    border:1px solid #262626;
    border-radius:4px;
    background:#101010;
    box-shadow:0 12px 40px rgba(0,0,0,.45);
  }

  .bottom-nav button {
    min-width:0;
    padding:0 4px;
    display:flex;
    align-items:center;
    justify-content:center;
    gap:5px;
    border:1px solid transparent;
    border-radius:3px;
    background:transparent;
    color:#93a0b4;
    font:inherit;
    font-size:12px;
    font-weight:800;
    white-space:nowrap;
    cursor:pointer;
  }

  .bottom-nav button.on { border-color:#303a4d; background:#161b24; color:#fff; }
}
</style>
